// --- Dependencies -------------------------------------------------------------------------------
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useResourceTable from "@/hooks/useResourceTable";
import useTranslation from "@/hooks/useTranslation";
import type { TyStoreRow, TyStoreType, TySubscriptionStatus } from "@/types";
import StatusBadge from "@/UI/atoms/StatusBadge";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import DataTable from "@/UI/components/DataTable";
import StatsSection from "@/UI/components/StatsSection";
import type { TyColumn } from "@/UI/components/DataTable/types";
import ListScreen from "@/UI/components/ListScreen";
import FilterSelect from "@/UI/inputs/FilterSelect";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { formatDate, formatNumber } from "@/utils/format";

// --- Types --------------------------------------------------------------------------------------

type TyStats = { total: number; subscribed: number; paused: number; pending: number; noSubscription: number };

// --- Constants ----------------------------------------------------------------------------------

const STORE_TYPES: TyStoreType[] = ["cafe", "restaurant", "both", "other"];

const SUB_STATUSES: TySubscriptionStatus[] = ["pending", "active", "coming", "grace", "paused", "canceled", "ended"];

// --- Logic --------------------------------------------------------------------------------------

export default function StoresScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const navigate = useNavigate();
    const { canEdit } = usePermission();
    const { addComp, removeComp } = usePopup();

    const [tab, setTab] = useState("");
    const [search, setSearch] = useState("");
    const [type, setType] = useState("");
    const [status, setStatus] = useState("");

    /**
     * The tabs are the three divisions worth a click; every other status lives in the filter panel as a
     * labelled dropdown. `""` is "all", which is why the tab value and the status filter are kept apart
     * — picking a tab must not silently clear a status the admin chose in the panel.
     */
    const tabStatus = tab === "subscribed" ? "active" : tab === "lapsed" ? "paused" : "";

    const table = useResourceTable<TyStoreRow, TyStats>({
        path: "stores",
        rowsKey: "stores",
        query: { search, type, status: status || tabStatus },
    });

    const isEditable = canEdit("stores");

    const stats = useMemo(() => {
        if (!table.stats) return [];
        return [
            { key: "total", label: t.stores.statTotal, value: formatNumber(table.stats.total, lang), tone: "brand" as const },
            { key: "subscribed", label: t.stores.statSubscribed, value: formatNumber(table.stats.subscribed, lang), tone: "good" as const },
            { key: "paused", label: t.stores.statPaused, value: formatNumber(table.stats.paused, lang), tone: "bad" as const },
            { key: "pending", label: t.stores.statPending, value: formatNumber(table.stats.pending, lang), tone: "warn" as const },
            { key: "none", label: t.stores.statNoSub, value: formatNumber(table.stats.noSubscription, lang) },
        ];
    }, [table.stats, t, lang]);

    const columns = useMemo<TyColumn<TyStoreRow>[]>(
        () => [
            {
                key: "name",
                label: t.stores.colName,
                width: "minmax(180px, 1.6fr)",
                render: (row) => (
                    <span className="list-cell-avatar-wrap">
                        <span className="list-cell-avatar">
                            {row.logo ? (
                                <img src={row.logo} alt="" />
                            ) : (
                                <img className="list-cell-avatar-fallback" src="/icons/store-icon.svg" alt="" />
                            )}
                        </span>
                        <span className="list-cell-strong">{row.name}</span>
                    </span>
                ),
                /* Single-row only on the server — one name across a selection is never intended. */
                edit: isEditable ? { kind: "text", value: (row) => row.name } : undefined,
            },
            {
                key: "slug",
                label: t.stores.colSlug,
                width: "minmax(130px, 1fr)",
                secondary: true,
                render: (row) => (
                    <span className="list-cell-mono" dir="ltr">
                        {row.slug}
                    </span>
                ),
                /* The live subdomain and the printed QR target — unique, so single-row only. */
                edit: isEditable ? { kind: "text", value: (row) => row.slug } : undefined,
            },
            {
                key: "type",
                label: t.stores.colType,
                width: "150px",
                render: (row) => <span>{t.enums.storeType[row.type]}</span>,
                edit: isEditable
                    ? { kind: "select", options: STORE_TYPES.map((one) => ({ value: one, label: t.enums.storeType[one] })), value: (row) => row.type }
                    : undefined,
            },
            {
                key: "subscription",
                label: t.stores.colSubscription,
                width: "140px",
                render: (row) => <StatusBadge status={row.subscription?.status ?? null} />,
            },
            {
                key: "branchesCount",
                label: t.stores.colBranches,
                width: "90px",
                align: "center",
                secondary: true,
                render: (row) => <span>{formatNumber(row.branchesCount, lang)}</span>,
            },
            {
                key: "productsCount",
                label: t.stores.colProducts,
                width: "90px",
                align: "center",
                secondary: true,
                render: (row) => <span>{formatNumber(row.productsCount, lang)}</span>,
            },
            {
                key: "createdAt",
                label: t.stores.colCreated,
                width: "120px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{formatDate(row.createdAt, lang)}</span>,
            },
        ],
        [t, lang, isEditable]
    );

    const confirmDelete = (ids: string[], label: string) => {
        removeComp("confirm-delete-store");
        addComp({
            name: "confirm-delete-store",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.stores.deleteWarning} — ${label}`,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.table.deleteConfirm,
                cancelLabel: t.table.deleteCancel,
                onConfirm: () => table.deleteBulk(ids),
            },
        });
    };

    return (
        <ListScreen writeError={table.writeError}>
            <StatsSection loading={table.loading && !table.stats} stats={stats} />

            <ActionsBar
                tabs={[
                    { value: "", label: t.stores.tabAll },
                    { value: "subscribed", label: t.stores.tabSubscribed },
                    { value: "lapsed", label: t.stores.tabLapsed },
                ]}
                tab={tab}
                onTab={setTab}
                search={{ value: search, onChange: setSearch, placeholder: t.stores.searchPlaceholder }}
                activeFilterCount={[type, status].filter(Boolean).length}
                onClearFilters={() => {
                    setType("");
                    setStatus("");
                }}
                filters={
                    <>
                        <FilterSelect
                            label={t.stores.colType}
                            value={type}
                            onChange={setType}
                            options={STORE_TYPES.map((one) => ({ value: one, label: t.enums.storeType[one] }))}
                        />
                        <FilterSelect
                            label={t.stores.colSubscription}
                            value={status}
                            onChange={setStatus}
                            options={[
                                ...SUB_STATUSES.map((one) => ({ value: one, label: t.enums.subscriptionStatus[one] })),
                                { value: "none", label: t.stores.filterNone },
                            ]}
                        />
                    </>
                }
            />

            <DataTable<TyStoreRow>
                rows={table.rows}
                columns={columns}
                rowId={(row) => row._id}
                loading={table.loading}
                error={table.error}
                onRetry={table.refetch}
                emptyTitle={t.stores.empty}
                emptyHint={t.stores.emptyHint}
                pager={{ page: table.page, lastPage: table.lastPage, total: table.total, onPage: table.setPage }}
                selection={isEditable ? { selected: table.selected, onChange: table.setSelected } : undefined}
                onCellSave={isEditable ? (row, key, value) => table.saveCell(row._id, key, value) : undefined}
                rowActions={(row) => (
                    <>
                        <button type="button" className="list-row-btn" onClick={() => navigate(`/stores/${row._id}`)} aria-label={t.table.preview}>
                            <img src="/icons/eye-opened-icon.svg" alt="" width={16} height={16} />
                        </button>
                        {isEditable && (
                            <button
                                type="button"
                                className="list-row-btn list-row-btn--danger"
                                onClick={() => confirmDelete([row._id], row.name)}
                                aria-label={t.table.deleteRow}
                            >
                                <img src="/icons/delete-icon.svg" alt="" width={15} height={15} />
                            </button>
                        )}
                    </>
                )}
            />

            {isEditable && (
                <BulkBar
                    count={table.selected.length}
                    busy={table.busy}
                    fields={[
                        {
                            key: "type",
                            label: t.stores.colType,
                            kind: "select",
                            options: STORE_TYPES.map((one) => ({ value: one, label: t.enums.storeType[one] })),
                        },
                    ]}
                    onApply={table.applyBulk}
                    onDelete={() => confirmDelete(table.selected, `${table.selected.length}`)}
                    onClear={() => table.setSelected([])}
                />
            )}
        </ListScreen>
    );
}
